import React, {useState} from "react"
import {default as cx} from "classnames"
import StatBox from "../../components/educator/StatBox"
import {PreceptorFeedbackScores} from "../../components/preceptor/PreceptorFeedbackScores"
import {fetcherGet, getInitials} from "../../utilities/general.jsx"
import {BarChart} from "../../components/charts/BarChart"
import {ChartLegend} from "../../components/charts/ChartLegend"
import { useChartColors } from "../../components/charts/useChartColors"

const EducatorPreceptorAccordionRow = ({preceptor}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [successRateData, setSuccessRateData] = useState(null)
    const [feedbackData, setFeedbackData] = useState(null)
    const [weeklyChartData, setWeeklyChartData] = useState(null)
    const [successRateLoading, setSuccessRateLoading] = useState(false)
    const collapseId = `preceptor-details-${preceptor.id}`

    const { secondaryColor, primaryColor } = useChartColors()

    const handleToggle = () => {
        const nextOpen = !isOpen
        setIsOpen(nextOpen)

        if (nextOpen && successRateData === null && !successRateLoading) {
            setSuccessRateLoading(true)
            fetcherGet(`/preceptors/preceptor/${preceptor.id}/orientee-success-rate`)
                .then((data) => {
                    setSuccessRateData(data)
                })
                .catch(() => {
                    console.error("Error fetching orientee success rate for preceptor", preceptor.id)
                    setSuccessRateData(null)
                })
                .finally(() => {
                    setSuccessRateLoading(false)
                })

            fetcherGet(`/preceptors/preceptor/${preceptor.id}/feedback-scores`)
                .then((data) => {
                    setFeedbackData(data)
                })
                .catch(() => {
                    setFeedbackData({questions: [], overall_grade: null})
                })

            fetcherGet(`/preceptors/preceptor/${preceptor.id}/weekly-ratings-chart`)
                .then((data) => setWeeklyChartData(data))
                .catch(() => setWeeklyChartData({weeks: [], preceptor_scores: [], group_scores: []}))
        }
    }

    return (
        <div className="card mt-4 border border-gray-4000">
            <div className="card-header collapsible collapsed border-0">
                <div className="card-title text-dark fw-bold gap-4">
                    <div className="d-flex flex-row">
                        <div className="symbol symbol-30px mt-1 me-2">
                            <div className="symbol-label fs-5 fw-semibold bg-primary text-inverse-danger">
                                {getInitials(preceptor.name)}
                            </div>
                        </div>
                        <div className="d-flex flex-column fs-5">
                            <div className="d-flex flex-row">{preceptor.name}</div>
                            <div className="d-flex flex-row">
                                <span className="text-dark fw-semibold d-block fs-7">{preceptor.unit}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-toolbar gap-4 flex-grow-1 flex-md-grow-0 flex-end rotate">
                    <button
                        type="button"
                        onClick={handleToggle}
                        aria-expanded={isOpen}
                        aria-controls={collapseId}
                        className={cx("btn flex-center flex-md-grow-0", isOpen ? "btn-primary" : "btn-light-primary")}
                    >
                        Details
                        <span className="px-2">
                            <i className={cx("fas fs-3", isOpen ? "fa-arrow-circle-up" : "fa-arrow-circle-down")} />
                        </span>
                    </button>
                </div>
            </div>

            {isOpen && (
                <div id={collapseId} className="card-body px-0">
                    {successRateLoading ? (
                        <div className="text-gray-600 fs-6 px-4">Loading...</div>
                    ) : (
                        <>
                            <div className="row">
                                <div className="col-12">
                                    <div className="card card-flush h-auto h-lg-100 mb-5 mb-lg-0 border-0">
                                        <div className="card-body pt-0">
                                            <div className="d-flex flex-wrap gap-4">
                                                <StatBox
                                                    value={
                                                        successRateData?.success_rate == null
                                                            ? "N/A"
                                                            : `${successRateData.success_rate}%`
                                                    }
                                                    label="Orientees' Success Rate"
                                                />
                                                <StatBox
                                                    value={successRateData?.total ?? 0}
                                                    label="Total Orientations Completed"
                                                />
                                                <StatBox
                                                    value={successRateData?.successful ?? 0}
                                                    label="Successful Orientations"
                                                />
                                                <StatBox
                                                    value={successRateData?.unsuccessful ?? 0}
                                                    label="Unsuccessful Orientations"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {feedbackData?.overall_grade != null && (
                                    <div className="col-12">
                                        <div className="card card-flush border-0">
                                            <div className="card-header">
                                                <div className="m-0">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fs-1 fw-bold text-gray-800 me-2 lh-1 ls-n2">
                                                            {feedbackData.overall_grade}%
                                                        </span>
                                                    </div>
                                                    <span className="fs-6 fw-semibold text-gray-700">
                                                        Overall Preceptor Grade
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="card-body pt-0">
                                                {weeklyChartData && weeklyChartData.weeks.length > 0 && (
                                                    <>
                                                        <ChartLegend
                                                            items={[
                                                                {
                                                                    label: "All Preceptors Weekly Score During Orientation Cycle",
                                                                    color: secondaryColor,
                                                                },
                                                                {
                                                                    label: "F. Last Weekly Average Feedback Grade",
                                                                    color: primaryColor,
                                                                },
                                                            ]}
                                                        />
                                                        <BarChart
                                                            xData={weeklyChartData.weeks}
                                                            ySeries={[
                                                                {
                                                                    name: "All Preceptors Weekly Score During Orientation Cycle",
                                                                    values: weeklyChartData.group_scores,
                                                                    width: 0.5,
                                                                },
                                                                {
                                                                    name: "F. Last Weekly Average Feedback Grade",
                                                                    values: weeklyChartData.preceptor_scores,
                                                                    width: 0.22,
                                                                },
                                                            ]}
                                                            yMax={4}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <div className="card card-flush h-auto h-lg-100 mb-5 mb-lg-0 border-0">
                                        <div className="card-header">
                                            <div className="m-0">
                                                <div className="d-flex align-items-center">
                                                    <span className="fs-1 fw-bold me-2">Feedback Scores</span>
                                                </div>
                                                <span className="fs-6 fw-semibold text-gray-700">
                                                    A snapshot look at average topic scores and month-to-month trends.
                                                </span>
                                            </div>
                                        </div>
                                        <div className="card-body py-0">
                                            <PreceptorFeedbackScores
                                                scores={feedbackData?.questions ?? []}
                                                isLoading={feedbackData === null}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default EducatorPreceptorAccordionRow;
